<template>
  <div class="container-fluid p-0" v-if="user">
    <div class="row">
      <div class="col-12">
        <div class="card my-4">
          <div class="card-header p-0 position-relative mt-n4 mx-3 z-index-2">
            <div
              class="bg-gradient-success shadow-success border-radius-lg p-4 d-flex justify-content-between align-items-center"
            >
              <h6 class="text-white text-capitalize m-0">
                Usuários ({{ totalUsers }})
              </h6>
              <download-excel
                ref="baixarXls"
                class="baixarXls"
                :data="userCsv"
                type="csv"
                name="usuarios.xls"
              >
                Baixar Lista
              </download-excel>
            </div>
          </div>

          <div class="card-body px-0 pb-2">
            <div class="container">
              <div class="row align-items-end">
                <div class="col-md-6">
                  <DefaultInput
                    @keyup="searchUser"
                    v-model="search"
                    label="Pesquisar"
                    placeholder="Pesquisar usuário"
                  />
                </div>
                <div class="col-md-4 mt-3 mt-md-0">
                  <label class="form-label text-xs text-uppercase text-secondary font-weight-bolder mb-1">
                    Plano
                  </label>
                  <select
                    class="form-select form-select-sm"
                    v-model="planFilter"
                    @change="onPlanFilterChange"
                    style="border: 1px solid #d2d6da !important;"
                  >
                    <option value="">Todos</option>
                    <option value="mensal">Mensal (ativos + vencidos)</option>
                    <option value="mensal_ativo">Mensal ativo</option>
                    <option value="mensal_vencido">Mensal vencido</option>
                    <option value="anual">Anual</option>
                    <option value="vitalicio">Vitalício (Diamond)</option>
                    <option value="com_plano">Com plano ativo</option>
                    <option value="sem_plano">Sem plano</option>
                  </select>
                </div>
                <div class="col-md-2 mt-3 mt-md-0 d-flex align-items-center justify-content-md-end pb-1">
                  <div class="form-check form-switch mb-0">
                    <input
                      class="form-check-input"
                      type="checkbox"
                      id="onlyMensalFilter"
                      :checked="planFilter === 'mensal' || planFilter === 'mensal_ativo' || planFilter === 'mensal_vencido'"
                      @change="toggleMensalOnly"
                    />
                    <label
                      class="form-check-label mb-0 text-xs text-uppercase text-body font-weight-bolder"
                      for="onlyMensalFilter"
                      style="cursor: pointer;"
                    >
                      Só mensal
                    </label>
                  </div>
                </div>
              </div>
            </div>
            <div class="table-responsive p-0" v-if="users">
              <table class="table align-items-center mb-0">
                <thead>
                  <tr>
                    <th
                      class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7"
                    >
                      Usuário / CPF
                    </th>
                    <th
                      class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 ps-2"
                    >
                      Contato
                    </th>
                    <th
                      class="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7"
                    >
                      Origem
                    </th>
                    <th
                      class="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7"
                    >
                      Plano
                    </th>
                    <th
                      class="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7"
                    >
                      Carteira
                    </th>
                    <th
                      class="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7"
                    >
                      Status
                    </th>
                    <th class="text-secondary opacity-7"></th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(r, index) in users" :key="r.id">
                    <td>
                      <div class="d-flex px-3 py-1">
                        <div>
                          <div class="avatar avatar-sm me-3 border-radius-lg bg-gradient-dark d-flex align-items-center justify-content-center shadow-dark">
                             <i class="material-icons text-white text-sm">person</i>
                          </div>
                        </div>
                        <div class="d-flex flex-column justify-content-center">
                          <h6 class="mb-0 text-sm font-weight-bolder">{{ r.full_name }}</h6>
                          <p class="text-xxs text-secondary mb-0">CPF: {{ r.document || r.Document || '---' }}</p>
                        </div>
                      </div>
                    </td>
                    <td class="align-middle">
                      <div class="d-flex flex-column text-xs">
                        <template v-if="isLgpdDeleted(r)">
                          <span class="font-weight-bold text-dark">{{
                            r.deleted_email || "—"
                          }}</span>
                          <span class="text-secondary">{{
                            r.deleted_phone || r.phone || "—"
                          }}</span>
                          <span class="text-xxs text-secondary mt-1"
                            >ID {{ r.id }} · anonimizado</span
                          >
                        </template>
                        <template v-else>
                          <span class="font-weight-bold text-dark">{{
                            r.email
                          }}</span>
                          <span class="text-secondary">{{ r.phone }}</span>
                        </template>
                      </div>
                    </td>

                    <td class="align-middle text-center">
                      <span
                        v-if="r.signup_source"
                        class="badge badge-sm bg-gradient-primary border-radius-lg text-uppercase"
                      >
                        {{ formatSignupSource(r.signup_source) }}
                      </span>
                      <span v-else class="text-xxs text-secondary">—</span>
                    </td>

                    <td class="align-middle text-center">
                      <div v-if="r.use_plan" class="d-flex flex-column align-items-center">
                        <span :class="getPlanBadgeClass(r)" class="px-3 border-radius-lg">
                          {{ getPlanLabel(r) }}
                        </span>
                        <p v-if="(r.plan_active === 'MENSAL' || r.plan_active === 'ANUAL') && r.plan_expiration" class="text-xxs text-secondary mb-0 mt-1">
                          Vence em: {{ new Date(r.plan_expiration).toLocaleDateString() }}
                        </p>
                      </div>
                      <span v-else class="text-xxs text-secondary font-weight-bold uppercase">SEM PLANO</span>
                    </td>

                    <td class="align-middle text-center">
                      <span v-if="r.system_wallet" class="badge badge-sm bg-gradient-info shadow-info border-radius-lg">ATIVA</span>
                      <span v-else class="badge badge-sm bg-gradient-secondary border-radius-lg">INATIVA</span>
                    </td>

                    <td class="align-middle text-center">
                      <span
                        class="badge badge-sm bg-gradient-dark shadow-dark border-radius-lg"
                        v-if="isLgpdDeleted(r)"
                        >EXCLUÍDA (LGPD)</span
                      >
                      <span
                        class="badge badge-sm bg-gradient-warning shadow-warning border-radius-lg"
                        v-else-if="r.token != null && !r.verified"
                        >AGUARDANDO</span
                      >
                      <span
                        class="badge badge-sm bg-gradient-success shadow-success border-radius-lg"
                        v-else-if="r.verified"
                        >ATIVO</span
                      >
                      <span
                        class="badge badge-sm bg-gradient-danger shadow-danger border-radius-lg"
                        v-else
                        >BLOQUEADO</span
                      >
                    </td>
                    <td class="align-middle">
                      <div class="d-flex align-items-center justify-content-center">
                        <button 
                          class="btn btn-link text-info p-0 m-0 me-3" 
                          @click="manageUser(r)"
                          v-tooltip.top="'Gerenciar Usuário'"
                        >
                          <i class="material-icons text-lg">admin_panel_settings</i>
                        </button>
                        <button class="more-btn p-0 m-0" @click="toggle(index, $event)">
                          <i class="material-icons text-secondary">more_vert</i>
                        </button>
                      </div>
                      <PrimeMenu
                        ref="menu"
                        :id="`overlay_menu_${index}`"
                        :model="[
                          {
                            label: 'Ver Detalhes',
                            icon: 'pi pi-user-edit',
                            command: () => {
                              this.manageUser(r);
                            },
                          },
                          ...items,
                          {
                            label: 'Logar como este usuário',
                            command: () => {
                              this.loginAsUser(r);
                            },
                          },
                          {
                            label: 'Deslogar usuário',
                            command: () => {
                              this.forceLogoutUser(r);
                            },
                          },
                          {
                            label: 'Desativar Carteira do Sistema',
                            disabled: !r.system_wallet,
                            command: () => {
                              if (r.system_wallet) {
                                this.deactivateSystemWallet(r);
                              }
                            },
                          },
                          {
                            label: 'Chamar no WhatsApp',
                            command: () => {
                              this.openWhatsapp(r.phone, r);
                            },
                          },
                          ...(!r.verified && r.token == null && !this.isLgpdDeleted(r)
                            ? [
                                {
                                  label: 'Desbloquear',
                                  command: () => {
                                    this.unblockUser(r.id);
                                  },
                                },
                              ]
                            : []),
                          // Bloquear: ATIVO, AGUARDANDO ou estado estranho (verified+token)
                          ...((r.verified || r.token != null) && !this.isLgpdDeleted(r)
                            ? [
                                {
                                  label: 'Bloquear',
                                  command: () => {
                                    this.blockUser(r.id);
                                  },
                                },
                              ]
                            : []),
                          ...(r.token != null && !r.verified
                            ? [
                                {
                                  label: 'Marcar como verificado',
                                  command: () => {
                                    this.markVerified(r.id);
                                  },
                                },
                              ]
                            : []),
                        ]"
                        :popup="true"
                      />
                    </td>
                  </tr>
                </tbody>
              </table>
              <!-- PAGINAÇÃO -->
              <!-- PAGINAÇÃO -->
              <Paginator
                v-if="totalPages > 1"
                v-model:first="currentPageOffset"
                :rows="1"
                :totalRecords="totalPages"
                @page="onPageChange"
              />
            </div>

            <div class="no-campaign" v-else>
              <i class="material-icons">summarize</i>
              <span>Você ainda não criou nenhuma campanha.</span>
              <span
                >Para criar, basta clicar em <strong>criar campanha</strong> e
                preencher os dados solicitados.</span
              >
              <span></span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <Loading v-if="loading" />

    <UserDetailsModal
      :isOpen="userModalOpen"
      :userId="selectedUserId"
      @close="userModalOpen = false"
      @updated="getUsers(currentPage)"
    />

    <SetWinnersDialog
      :isOpen="winnersDialog"
      :isLoading="isLoadingPrizes"
      :prizes="prizes"
      :campaignId="raffleId"
      :isDetails="isDetails"
      @close="winnersDialog = false"
      @endCampaign="getUsers(currentPage)"
    />
  </div>
</template>

<script>
import UsersHttp from "@/http/UsersHttp.js";
import LoggingMixin from "@/mixins/LoggingMixin.js";
import Loading from "@/components/Loading.vue";
import PrimeMenu from "primevue/menu";
import SetWinnersDialog from "./components/SetWinnersDialog.vue";
import DefaultInput from "@/components/DefaultInput.vue";
import UserDetailsModal from "./components/UserDetailsModal.vue";
import { Paginator } from "primevue";
import { toWhatsAppNumber } from "@/helpers/phoneNumber.js";

export default {
  name: "users",
  mixins: [LoggingMixin],
  components: {
    Loading,
    PrimeMenu,
    SetWinnersDialog,
    DefaultInput,
    UserDetailsModal,
    Paginator
  },
  data() {
    return {
      usersRaw: null,
      currentPage: 1,
      currentPageOffset: 0,
      totalPages: 1,
      totalUsers: 0,
      users: null,
      user: null,
      userCsv: null,
      search: "",
      planFilter: "",
      raffles: [],
      loading: true,
      items: [],
      winnersDialog: false,
      isDetails: false,
      rafflePrizes: [],
      isLoadingPrizes: false,
      raffleId: null,
      userModalOpen: false,
      selectedUserId: null,
    };
  },
  async mounted() {
    this.user = this.$store.getters.user;
    await this.init();
  },
  watch: {
    search: function (val) {
      if (val.length > 3 || val == "") {
        this.currentPage = 1;
        this.getUsers(this.currentPage);
      }
    },
  },
  methods: {
    formatSignupSource(source) {
      const map = {
        google: "GOOGLE",
        instagram: "INSTAGRAM",
        facebook: "FACEBOOK",
        youtube: "YOUTUBE",
        tiktok: "TIKTOK",
        indicacao: "INDICAÇÃO",
        grupo: "GRUPO",
        anuncio: "ANÚNCIO",
        outro: "OUTRO",
      };
      return map[String(source || "").toLowerCase()] || String(source || "").toUpperCase();
    },
    async init() {
      this.loading = true;

      if (this.user.role == "ADMIN") {
        await this.getUsers(this.currentPage);
      }
      this.loading = false;
    },
    async getUsers(page) {
      try {
        this.currentPage = page;
        this.currentPageOffset = page - 1;
        this.users = null;
        this.loading = true;

        const params = {
          page: page || 1,
          search: this.search,
        };
        if (this.planFilter) {
          params.plan = this.planFilter;
        }
        const response = await UsersHttp.getUsers(params);
        this.usersRaw = response.data.users;
        this.users = response.data.users;
        this.totalPages = response.data.pages;
        this.totalUsers = response.data.total;
        this.loading = false;
        this.userCsv = (this.usersRaw || []).map((user) => ({
          full_name: user.full_name,
          email: user.email,
          phone: user.phone,
          origem: user.signup_source || "",
        }));
      } catch (error) {
        console.error(error);
        this.loading = false;
      }
    },
    async onPlanFilterChange() {
      this.currentPage = 1;
      await this.getUsers(1);
    },
    async toggleMensalOnly(event) {
      this.planFilter = event.target.checked ? "mensal" : "";
      this.currentPage = 1;
      await this.getUsers(1);
    },
    async onPageChange(event) {
      const page = event.page + 1;
      await this.getUsers(page);
    },
    isLgpdDeleted(user) {
      if (!user) return false;
      if (user.deleted_at) return true;
      const email = String(user.email || "");
      return email.startsWith("deleted+") && email.endsWith("@rifa365.invalid");
    },
    async blockUser(id) {
      try {
        this.$swal({
          title: "Tem certeza?",
          text: "Você deseja bloquear este usuário?",
          icon: "warning",
          showCancelButton: true,
          confirmButtonText: "Sim",
          cancelButtonText: "Cancelar",
          confirmButtonColor: "#e41b80",
          cancelButtonColor: "#6c757d",
        }).then(async (result) => {
          if (result.isConfirmed) {
            await this.block(id);
          }
        });
      } catch (error) {
        console.error(error);
      }
    },
    async block(id) {
      try {
        await UsersHttp.blockUser(id);
        const target = this.users?.find((u) => u.id === id);
        const targetName = target ? `${target.full_name} (${target.email})` : `ID ${id}`;
        this.$log.log("USUÁRIOS", "AÇÃO", `Administrador bloqueou a conta do usuário ${targetName}`);
        this.getUsers(this.currentPage);
        this.$toast.add({
          severity: "success",
          summary: "Usuário bloqueado.",
          detail: "O usuário foi bloqueado com sucesso.",
          life: 3000,
        });
      } catch (error) {
        console.error(error);
      }
    },
    async unblockUser(id) {
      try {
        this.$swal({
          title: "Tem certeza?",
          text: "Você deseja desbloquear este usuário?",
          icon: "warning",
          showCancelButton: true,
          confirmButtonText: "Sim",
          cancelButtonText: "Cancelar",
          confirmButtonColor: "#e41b80",
          cancelButtonColor: "#6c757d",
        }).then(async (result) => {
          if (result.isConfirmed) {
            await this.unblock(id);
          }
        });
      } catch (error) {
        console.error(error);
      }
    },
    async unblock(id) {
      try {
        await UsersHttp.unblockUser(id);
        const target = this.users?.find((u) => u.id === id);
        const targetName = target ? `${target.full_name} (${target.email})` : `ID ${id}`;
        this.$log.log("USUÁRIOS", "AÇÃO", `Administrador desbloqueou a conta do usuário ${targetName}`);
        this.getUsers(this.currentPage);
        this.$toast.add({
          severity: "success",
          summary: "Usuário desbloqueado.",
          detail: "O usuário foi desbloqueado com sucesso.",
          life: 3000,
        });
      } catch (error) {
        console.error(error);
      }
    },
    async markVerified(id) {
      try {
        this.$swal({
          title: "Tem certeza?",
          text: "Você deseja marcar este usuário como verificado?",
          icon: "warning",
          showCancelButton: true,
          confirmButtonText: "Sim",
          cancelButtonText: "Cancelar",
          confirmButtonColor: "#e41b80",
          cancelButtonColor: "#6c757d",
        }).then(async (result) => {
          if (result.isConfirmed) {
            await this.verify(id);
          }
        });
      } catch (error) {
        console.error(error);
      }
    },
    async verify(id) {
      try {
        await UsersHttp.markVerified(id);
        const target = this.users?.find((u) => u.id === id);
        const targetName = target ? `${target.full_name} (${target.email})` : `ID ${id}`;
        this.$log.log("USUÁRIOS", "AÇÃO", `Administrador marcou o usuário ${targetName} como verificado`);
        this.getUsers(this.currentPage);
        this.$toast.add({
          severity: "success",
          summary: "Usuário verificado.",
          detail: "O usuário foi verificado com sucesso.",
          life: 3000,
        });
      } catch (error) {
        console.error(error);
      }
    },
    toggle(index, event) {
      this.$refs.menu[index].toggle(event);
    },
    openWhatsapp(number, user) {
      if (user) {
        this.$log.log('USUÁRIOS', 'AÇÃO', `Administrador abriu WhatsApp para o usuário "${user.full_name}" (${user.phone})`);
      }
      const wa = toWhatsAppNumber(number);
      if (wa) window.open(`https://api.whatsapp.com/send?phone=${wa}`);
    },
    async loginAsUser(user) {
      try {
        this.$log.log("AUTH", "AÇÃO", `Administrador iniciou processo de login como o usuário "${user.full_name}" (${user.email})`);
        this.$swal({
          title: "Logar como usuário?",
          text: `Você será deslogado e logado como ${user.full_name}.`,
          icon: "warning",
          showCancelButton: true,
          confirmButtonText: "Sim, logar",
          cancelButtonText: "Cancelar",
          confirmButtonColor: "#e41b80",
          cancelButtonColor: "#6c757d",
        }).then(async (result) => {
          if (result.isConfirmed) {
            this.loading = true;
            try {
              const response = await UsersHttp.loginAsUser(user.id);
              this.$store.commit('setUser', response.data.user)
              this.$store.commit('setToken', response.data.token)
              
              this.$log.log("AUTH", "AÇÃO", `Administrador fez login como o usuário "${user.full_name}" (${user.email})`);

              this.$toast.add({
                severity: "success",
                summary: "Login realizado!",
                detail: `Você foi logado como ${user.full_name}.`,
                life: 2000,
              });

              // Redirecionar após 1.5s
              setTimeout(() => {
                if (response.data.user.role === "ADMIN") {
                  window.location.href = "/";
                } else {
                  window.location.href = "/raffles";
                }
              }, 1500);
            } catch (error) {
              console.error(error);
              this.loading = false;
              this.$toast.add({
                severity: "error",
                summary: "Erro!",
                detail:
                  error.response?.data?.error ||
                  "Não foi possível fazer login como este usuário.",
                life: 3000,
              });
            }
          }
        });
      } catch (error) {
        console.error(error);
      }
    },
    async forceLogoutUser(user) {
      try {
        this.$swal({
          title: "Deslogar usuário?",
          text: `Isso invalidará todas as sessões ativas de ${user.full_name}. Ele precisará logar novamente.`,
          icon: "warning",
          showCancelButton: true,
          confirmButtonText: "Sim, deslogar",
          cancelButtonText: "Cancelar",
          confirmButtonColor: "#e41b80",
          cancelButtonColor: "#6c757d",
        }).then(async (result) => {
          if (result.isConfirmed) {
            this.loading = true;
            try {
              await UsersHttp.forceLogoutUser(user.id);
              
              this.$log.log("USUÁRIOS", "AÇÃO", `Administrador forçou o logout do usuário "${user.full_name}" (${user.email})`);

              this.$toast.add({
                severity: "success",
                summary: "Usuário deslogado!",
                detail: `O usuário ${user.full_name} foi desconectado de todos os dispositivos.`,
                life: 3000,
              });
              
              this.loading = false;
            } catch (error) {
              console.error(error);
              this.loading = false;
              this.$toast.add({
                severity: "error",
                summary: "Erro!",
                detail: error.response?.data?.error || "Não foi possível deslogar o usuário.",
                life: 3000,
              });
            }
          }
        });
      } catch (error) {
        console.error(error);
      }
    },
    async deactivateSystemWallet(user) {
      try {
        this.$swal({
          title: "Desativar Carteira do Sistema?",
          text: `Você está prestes a desativar a carteira do sistema para ${user.full_name}.`,
          icon: "warning",
          showCancelButton: true,
          confirmButtonText: "Sim, desativar",
          cancelButtonText: "Cancelar",
          confirmButtonColor: "#e41b80",
          cancelButtonColor: "#6c757d",
        }).then(async (result) => {
          if (result.isConfirmed) {
            this.loading = true;
            try {
              // Atualizar perfil do usuário
              const updatedUser = { ...user, system_wallet: false };
              await UsersHttp.updateUserProfile(user.id, updatedUser);

              this.$log.log("USUÁRIOS", "AÇÃO", `Administrador desativou a Carteira do Sistema para o usuário "${user.full_name}" (${user.email})`);

              this.$toast.add({
                severity: "success",
                summary: "Carteira Desativada!",
                detail: `Carteira do sistema desativada para ${user.full_name}.`,
                life: 3000,
              });

              // Recarregar lista de usuários
              await this.getUsers(this.currentPage);
              this.loading = false;
            } catch (error) {
              console.error(error);
              this.loading = false;
              this.$toast.add({
                severity: "error",
                summary: "Erro!",
                detail:
                  error.response?.data?.error ||
                  "Não foi possível desativar a carteira do sistema.",
                life: 3000,
              });
            }
          }
        });
      } catch (error) {
        console.error(error);
      }
    },
    manageUser(user) {
      this.selectedUserId = user.id;
      this.userModalOpen = true;
    },
    getPlanLabel(user) {
      if (user.plan_active === 'VITALICIO') return 'DIAMOND';
      if (user.plan_active === 'ANUAL') {
        if (this.isPlanExpired(user)) return 'ANUAL VENCIDO';
        return 'ANUAL';
      }
      if (user.plan_active === 'MENSAL') {
        if (this.isPlanExpired(user)) return 'VENCIDO';
        return 'STARTER';
      }
      return 'SEM PLANO';
    },
    getPlanBadgeClass(user) {
      if (user.plan_active === 'VITALICIO') return 'badge badge-sm bg-gradient-primary shadow-primary border-radius-lg';
      if (user.plan_active === 'ANUAL') {
        if (this.isPlanExpired(user)) return 'badge badge-sm bg-gradient-danger shadow-danger border-radius-lg';
        return 'badge badge-sm bg-gradient-success shadow-success border-radius-lg';
      }
      if (user.plan_active === 'MENSAL') {
        if (this.isPlanExpired(user)) return 'badge badge-sm bg-gradient-danger shadow-danger border-radius-lg';
        return 'badge badge-sm bg-gradient-info shadow-info border-radius-lg';
      }
      return 'badge badge-sm bg-gradient-secondary border-radius-lg';
    },
    isPlanExpired(user) {
      if (!user.plan_expiration) return false;
      const expiration = new Date(user.plan_expiration);
      expiration.setHours(23, 59, 59, 999);
      return new Date() > expiration;
    },
  },
};
</script>

<style scoped lang="scss">
.campaign-btn {
  background-color: #f0f2f5;
  color: #e41b80;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  line-height: 0;

  .btn-icon {
    font-size: 1.6em;
  }

  &:hover {
    transform: scale(1.04);
    color: #e41b80;
  }
}

.more-btn {
  background-color: transparent;
  border: none;
  padding: 0;
  margin: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.no-campaign {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  width: 100%;
  margin-bottom: 15px;
  padding: 50px 0;
}

.filters {
  display: flex;
  justify-content: flex-end;
  margin-bottom: 15px;
}

.baixarXls {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 10px 15px;
  border-radius: 10px;
  background-color: #e41b80;
  color: white;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s;

  &:hover {
    background-color: #e41b80;
    color: white;
    transform: scale(1.05);
  }
}
.page-link {
  cursor: pointer;
}

.page-item {
  cursor: pointer;
  margin: 0 5px;
}
</style>
